import { gql } from "@apollo/client";
import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";
import { CURRENCY_FRAGMENT } from "../../fragments/menu";

import type {
  MenuNewResp,
  MenuNewVariables,
  MenuVariables,
  Menu
} from "../../types/menuTypes";

const CREATE_MENU = gql`
  mutation CreateMenu($attributes: CreateMenuInput!) {
    createMenu(attributes: $attributes) {
      menu {
        name
        languages {
          position
          language
        }
        mainLanguage
        description
        restaurantId
        ...CurrencyFragment
      }
      errors
    }
  }
  ${CURRENCY_FRAGMENT}
`;

type UseCreateMenuOptions = {
  onCompleted?: (menu: Menu) => void;
  onError?: (error: unknown) => void;
  mutationOptions?: Parameters<typeof useMutation<MenuNewResp, MenuNewVariables>>[1];
};

const useCreateMenu = (options?: UseCreateMenuOptions) => {
  const [mutate, { data, loading, error }] = useMutation<
    MenuNewResp,
    MenuNewVariables
  >(CREATE_MENU, options?.mutationOptions);

  const createMenu = useCallback(
    async (input: MenuVariables) => {
      try {
        const result = await mutate({
          variables: {
            attributes: {
              name: input.name,
              languages: input.languages,
              mainLanguage: input.mainLanguage,
              description: input.description,
              restaurantId: input.restaurantId,
              currency: input.currency
            },
          },
        });

        const createMenu = result?.data?.createMenu;

        if (createMenu?.errors?.length) {
          throw new Error(createMenu.errors.join(", "));
        }

        if (options?.onCompleted && createMenu?.menu) {
          options.onCompleted(createMenu.menu);
        }

        return createMenu?.menu;
      } catch (err) {
        if (options?.onError) {
          options.onError(err);
        }
        throw err;
      }
    },
    [mutate, options]
  );

  const menu = data?.createMenu?.menu;

  return {
    createMenu,
    createdMenu: menu,
    loading,
    error: error as Error | null | undefined,
  };
}

export default useCreateMenu
